import {useMemo} from 'react'
import {useStatesGlobal} from '../../../../_gori/helpers/components/StatesGlobalProvider'
import {
  getCarrierNamesFromServices,
  getCarrierSettingsKey,
  isCarrierApplicableForRoute,
} from '../components/order-child/rateHelpers'

interface UseCarrierNamesOptions {
  /** Whether the current order is international. When provided, carriers that
   *  don't support the route type are excluded from both lists. */
  isInternational?: boolean
}

/**
 * Derives enabled and disabled carrier name lists from the global carrier
 * settings. Handles the dhlecom → dhl mapping and route-type filtering.
 *
 * NOTE: "dhlecom" (DHL eCommerce) and "dhl" (DHL Express) are separate carriers
 * internally, but the carrier settings UI exposes a single "dhl" toggle that
 * controls both. We map dhlecom → dhl when looking up the settings key.
 */
export const useCarrierNames = ({isInternational}: UseCarrierNamesOptions = {}) => {
  const {statesGlobal} = useStatesGlobal()
  const carriers = statesGlobal.carriers
  const carrierRouteSettings = statesGlobal.carrierRouteSettings

  const carrierNames = useMemo(() => {
    const allCarriers = getCarrierNamesFromServices(carriers)
    let filtered = allCarriers

    // Filter by carrier route settings (enabled/disabled in settings)
    if (carrierRouteSettings?.carriers) {
      const settingsCarriers = carrierRouteSettings.carriers
      filtered = filtered.filter((name) => {
        return settingsCarriers[getCarrierSettingsKey(name)] !== false
      })
    }

    // Filter by route type (domestic vs international)
    if (isInternational !== undefined) {
      filtered = filtered.filter((name) =>
        isCarrierApplicableForRoute(carriers, name, isInternational)
      )
    }

    return filtered
  }, [carriers, carrierRouteSettings, isInternational])

  const disabledCarrierNames = useMemo(() => {
    if (!carrierRouteSettings?.carriers) return [] as string[]
    const allCarriers = getCarrierNamesFromServices(carriers)
    const settingsCarriers = carrierRouteSettings.carriers
    let filtered = allCarriers.filter((name) => {
      return settingsCarriers[getCarrierSettingsKey(name)] === false
    })

    // Filter by route type (domestic vs international)
    if (isInternational !== undefined) {
      filtered = filtered.filter((name) =>
        isCarrierApplicableForRoute(carriers, name, isInternational)
      )
    }

    return filtered
  }, [carriers, carrierRouteSettings, isInternational])

  return {carrierNames, disabledCarrierNames}
}
